import { initializeApp } from "firebase/app";
import { cuentaEnMetricas } from '../utils/clasificacionAlumnos';
import { fusionarUsuariosConClasificacion } from '../utils/clasificacionUsuarios';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import {
  initializeFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  updateDoc,
  setDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_APIKEY,
  authDomain: process.env.REACT_APP_AUTHDOMAIN,
  projectId: process.env.REACT_APP_PROJECTID,
  storageBucket: process.env.REACT_APP_STORAGEBUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGINSENDERID,
  appId: process.env.REACT_APP_APPID,
};

const requiredConfig = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
];
const missingConfig = requiredConfig.filter((key) => !firebaseConfig[key]);

let app;
let auth;
let db;
let storage;

if (missingConfig.length > 0) {
  console.error(
    "Firebase initialization error: Missing environment variables for:",
    missingConfig.join(", ")
  );
} else {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = initializeFirestore(app, {
    experimentalForceLongPolling: true,
    useFetchStreams: false,
  });
  storage = getStorage(app);
}

export { app, auth, db, storage };

export async function userExists(uid) {
  const docRef = doc(db, "users", uid);
  const res = await getDoc(docRef);
  return res.exists();
}

export const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe();
        resolve(user);
      },
      reject
    );
  });
};

export const createAuthUser = async (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
};

export async function singIn(email, password) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const res = await signInWithEmailAndPassword(auth, normalizedEmail, password);
  return res.user;
}

export async function signInWithFirebaseCustomToken(customToken) {
  const res = await signInWithCustomToken(auth, customToken);
  return res.user;
}

export async function existsUsername(username) {
  const users = [];
  const docsRef = collection(db, "users");
  const q = query(docsRef, where("username", "==", username));
  const usersSnapshot = await getDocs(q);

  usersSnapshot.forEach((docSnapshot) => {
    users.push(docSnapshot.data());
  });

  return users.length > 0 ? users[0].uid : null;
}

export async function registerNewUser(user) {
  try {
    const collectionRef = collection(db, "users");
    const docRef = doc(collectionRef, user.uid);
    await setDoc(docRef, user);
  } catch (error) {
    console.error("Error registering user:", error);
  }
}

export async function storeUser(docId, data) {
  try {
    const collectionRef = collection(db, "users");
    const docRef = doc(collectionRef, docId);
    await setDoc(docRef, data);
  } catch (error) {
    console.error(error);
  }
}

export async function updateUser(user) {
  try {
    const collectionRef = collection(db, "users");
    const docRef = doc(collectionRef, user.uid);
    await setDoc(docRef, user);
  } catch (error) {
    console.error("Error updating user:", error);
  }
}

export async function updateById(uid, data) {
  try {
    const docRef = doc(db, "users", uid);
    await updateDoc(docRef, data);
  } catch (error) {
    console.error(error);
  }
}

export async function getUserInfo(uid) {
  try {
    const docRef = doc(db, "users", uid);
    const document = await getDoc(docRef);
    return document.data();
  } catch (error) {
    console.error("Error getting user info:", error);
    return null;
  }
}

export async function setUserProfilePhoto(uid, file) {
  try {
    const imageRef = ref(storage, `profilePicture/${uid}`);
    await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(imageRef);
    return { downloadURL };
  } catch (error) {
    console.error("Error subiendo archivo de perfil:", error);
    return null;
  }
}

export async function obtenerCantidadUsuarios() {
  const usuarios = await obtenerUsuarios();
  return usuarios.filter(usuario => !usuario.soloClasificacion && cuentaEnMetricas(usuario)).length;
}

export async function obtenerUsuarios() {
  const usuariosColeccion = collection(db, "users");
  const [snapshot, clasificaciones] = await Promise.all([
    getDocs(usuariosColeccion), getDocs(collection(db, 'clasificacionUsuarios')),
  ]);
  return fusionarUsuariosConClasificacion(snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  })), clasificaciones.docs.map(item => ({ ...item.data(), id: item.id })));
}

export async function logout() {
  await auth.signOut();
}
